import { Component, Input, OnInit } from '@angular/core';
import { AuthorizerService } from '@markant-platform/authorizer/data-access';
import { Group, GroupRegistrationRequestDto } from '@markant-platform/authorizer/model';
import { catchError, distinctUntilChanged, EMPTY, Observable, of, switchMap } from 'rxjs';
import { Page, TableContext } from '@markant-platform/shared/data-access';
import { SharedDialogFeatureModule } from '@markant-platform/shared/dialog/feature';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import {
  EMPTY_PAGE,
  ENTRY_ID,
  TableActionElement,
  TableDraft,
  TableDraftBuilder,
} from '@markant-platform/shared/table/model';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedTableFeatureModule, Table } from '@markant-platform/shared/table/feature';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { MarkantElementsModule } from '@markant/elements-angular';
import { CreateNewGroupComponent } from './create-new-group/create-new-group.component';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { DEFAULT_SORT, ROLE_CONFIG, TABLE_HEADER_AND_CELLS } from './configuration/group-assignment.config';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DefaultValuePipe } from '@markant-platform/shared/util-pipes';
import { UiPageHeaderComponent } from '@markant-platform/shared/ui-components';
import { deepEqual } from '@markant-platform/shared/util-helpers';
import { MarkantSearchbarCategory } from '@markant/elements-angular/dist/directives/proxies';

@Component({
  selector: 'markant-platform-group-assignment',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    MarkantElementsModule,
    SharedTableFeatureModule,
    CreateNewGroupComponent,
    ModalBridgeComponent,
    SharedDialogFeatureModule,
    DefaultValuePipe,
    UiPageHeaderComponent,
  ],
  templateUrl: './group-assignment.component.html',
  styleUrls: ['./group-assignment.component.scss'],
})
export class GroupAssignmentComponent extends Table implements OnInit {
  //if used as a demo component this value will be used instead of fetched data
  @Input() demoContent?: Group[];

  isAddUserToGroupDialogOpen = false;
  isCreateNewGroupDialogOpen = false;
  groupToAdd?: Group;

  selectedCategory: 'group' | 'member' = 'group';
  searchbarCategories = ['group', 'member'].map(category => ({
    value: category,
    representation: this.translocoService.translate('common.' + category),
    selected: this.selectedCategory === category,
  })) as MarkantSearchbarCategory[];
  headline?: string;

  private goTo = (id: string): void => {
    this.router.navigate([id], { relativeTo: this.route });
  };

  override totalCount = 0;

  override tableDraft: TableDraft = new TableDraftBuilder(TABLE_HEADER_AND_CELLS)
    .sortableColumns(['displayName', 'createdAt'])
    .sort(DEFAULT_SORT)
    .rowLink(['./', ENTRY_ID])
    .actions([
      {
        icon: 'm_arrow-right',
        colorScheme: 'secondary',
        size: '11',
        function: this.goTo as (...args: unknown[]) => unknown,
      } as TableActionElement,
    ])
    .build();

  constructor(
    private readonly authorizer: AuthorizerService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly snackBarService: SnackbarService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly translocoService: TranslocoService
  ) {
    super(TableContext.GROUP_ASSIGNMENT);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.tableParams$.next({
      ...this.tableParams$.getValue(),
      searchQuery: '',
    });

    this.authenticatedUserService
      .getMatchingConfigForCurrentRouteAndRole(ROLE_CONFIG)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ additionalConfig }) => {
        this.headline = additionalConfig['headline'][0];
      });

    this.translocoService.langChanges$
      .pipe(
        switchMap(() => this.tableParams$.pipe(distinctUntilChanged(deepEqual))),
        switchMap(() => {
          if (this.demoContent) {
            return of({
              ...EMPTY_PAGE,
              result: this.demoContent,
              totalCount: this.demoContent.length,
            } as Page<Group>);
          }

          return this.getPagedGroups();
        }),
        catchError(() => {
          return of(EMPTY_PAGE as Page<Group>);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ totalCount, result }) => {
        this.totalCount = totalCount;
        this.reload(result);
      });
  }

  changeSearchCategory(event: CustomEventInit): void {
    this.selectedCategory = event.detail.value;

    if (this.tableParams$.getValue().searchQuery.length) {
      this.getPagedGroups()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(({ result }) => {
          this.tableData$.next(this.tableService.returnMappedTableData(result, this.tableDraft));
        });
    }
  }

  openAddUserModal(group: Group): void {
    this.groupToAdd = group;
    this.isAddUserToGroupDialogOpen = true;
  }

  createNewGroup(groupName: string): void {
    this.isLoading$.next(true);
    const groupRegistrationRequestDto: GroupRegistrationRequestDto = {
      displayName: groupName,
    };

    this.authorizer
      .createGroup(groupRegistrationRequestDto)
      .pipe(
        catchError(() => {
          this.isLoading$.next(false);
          this.snackBarService.showErrorSnackbarWithTransloco(
            'dashboardOverview.groupAssignment.errorDialog.createGroup',
            { groupName: groupName }
          );
          return EMPTY;
        })
      )
      .subscribe(({ id }) => {
        this.isLoading$.next(false);
        this.snackBarService.showSuccessSnackbarWithTransloco(
          'dashboardOverview.groupAssignment.successMessages.addedGroup',
          { groupName: groupName }
        );
        this.router.navigate([id], { relativeTo: this.route });
      });
  }

  toggleCreateGroupDialog(isOpen: boolean): void {
    this.isCreateNewGroupDialogOpen = isOpen;
  }

  private getPagedGroups(): Observable<Page<Group>> {
    return this.authorizer.getPagedGroups(
      this.tableParams$.value,
      this.tableParams$.value.searchQuery.length
        ? { query: this.tableParams$.value.searchQuery, category: this.selectedCategory }
        : undefined
    );
  }
}
